import React, { useState, useEffect } from 'react';
import api from '../../api/axiosConfig';
import { FileText, Printer, Calendar, IndianRupee, X } from 'lucide-react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PageLayout, { PageHeader } from '../../components/common/PageLayout';

const fmt = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(val || 0);

export default function MyPayslips() {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlip, setSelectedSlip] = useState(null);

  const fetchPayslips = async () => {
    try {
      setLoading(true);
      const res = await api.get('/workforce/payroll/my');
      setPayslips(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load your payslips.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayslips();
  }, []);

  // Opens the browser's native print dialog for the slip
  const handlePrint = () => {
    window.print();
  };

  return (
    <PageLayout>
      
      <PageHeader
        icon={DocumentTextIcon}
        iconColor="text-[#E8612C]"
        title="My Payslips"
        subtitle="View and download your monthly salary slips and payment history."
        onRefresh={fetchPayslips}
        loading={loading}
      />

        {loading ? (
          <div className="text-center p-12 text-gray-500">Loading your payroll history...</div>
        ) : payslips.length === 0 ? (
          <div className="bg-white p-8 rounded-xl border border-gray-200 text-center shadow-sm">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-700">No Payslips Available</h3>
            <p className="text-gray-500 text-sm mt-1">Your finalized payslips will appear here once processed by HR.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {payslips.map(slip => (
              <div key={slip.id} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:border-blue-300 transition cursor-pointer" onClick={() => setSelectedSlip(slip)}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">Pay Period</p>
                    <p className="text-lg font-extrabold text-gray-900">{slip.payroll_month}</p>
                  </div>
                  <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded">Finalized</span>
                </div>
                <div className="border-t border-gray-100 pt-4 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Net Transfer</span>
                  <span className="text-lg font-bold text-emerald-600">{fmt(slip.net_pay)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* --- PAYSLIP MODAL / PRINT VIEW --- */}
      {selectedSlip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 print:p-0 print:bg-white print:block">
          <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl overflow-hidden print:shadow-none print:max-w-full">
            
            {/* Modal Actions (Hidden when printing) */}
            <div className="bg-gray-100 px-6 py-3 border-b border-gray-200 flex justify-between items-center print:hidden">
              <h2 className="font-bold text-gray-700">Payslip - {selectedSlip.payroll_month}</h2>
              <div className="flex gap-3">
                <button onClick={handlePrint} className="flex items-center gap-2 bg-white border border-gray-300 px-4 py-1.5 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50">
                  <Printer size={16} /> Print / Save PDF
                </button>
                <button onClick={() => setSelectedSlip(null)} className="p-1.5 bg-gray-200 text-gray-600 rounded-lg hover:bg-red-100 hover:text-red-600">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* THE ACTUAL PRINTABLE PAYSLIP */}
            <div className="p-8 print:p-0">
              {/* Company Header */}
              <div className="text-center mb-8 border-b-2 border-gray-800 pb-6">
                <h1 className="text-2xl font-black text-gray-900 uppercase tracking-widest">Your Construction ERP Ltd.</h1>
                <p className="text-sm text-gray-500">123 Corporate Avenue, Tech Park, City, 400001</p>
                <h2 className="text-lg font-bold text-gray-800 mt-4 border border-gray-300 inline-block px-6 py-1 rounded bg-gray-50">SALARY SLIP: {selectedSlip.payroll_month}</h2>
              </div>

              {/* Employee Details Grid */}
              <div className="grid grid-cols-2 gap-6 mb-8 text-sm border-b border-gray-200 pb-6">
                <div>
                  <div className="flex justify-between mb-2"><span className="font-bold text-gray-600">Employee Name:</span> <span className="font-medium text-gray-900">{selectedSlip.name}</span></div>
                  <div className="flex justify-between mb-2"><span className="font-bold text-gray-600">Employee ID:</span> <span className="font-medium text-gray-900">EMP-{selectedSlip.employee_id}</span></div>
                  <div className="flex justify-between mb-2"><span className="font-bold text-gray-600">Department:</span> <span className="font-medium text-gray-900">{selectedSlip.department}</span></div>
                </div>
                <div>
                  <div className="flex justify-between mb-2"><span className="font-bold text-gray-600">Bank Name:</span> <span className="font-medium text-gray-900">{selectedSlip.bank_name || 'N/A'}</span></div>
                  <div className="flex justify-between mb-2"><span className="font-bold text-gray-600">Account No:</span> <span className="font-medium text-gray-900">{selectedSlip.bank_account_no || 'N/A'}</span></div>
                  <div className="flex justify-between mb-2"><span className="font-bold text-gray-600">Days Present / Absent:</span> <span className="font-medium text-gray-900">{selectedSlip.days_present} / {selectedSlip.days_absent}</span></div>
                </div>
              </div>

              {/* Earnings & Deductions Table */}
              <div className="grid grid-cols-2 gap-8 mb-8 border border-gray-300 rounded-lg overflow-hidden">
                {/* EARNINGS */}
                <div>
                  <div className="bg-gray-100 px-4 py-2 font-bold border-b border-gray-300 text-gray-800">Earnings</div>
                  <div className="px-4 py-3 flex justify-between text-sm"><span className="font-medium">Basic Pay</span> <span>{fmt(selectedSlip.base_pay)}</span></div>
                  <div className="px-4 py-3 flex justify-between text-sm bg-gray-50"><span className="font-medium">Overtime Pay</span> <span>{fmt(selectedSlip.overtime_pay)}</span></div>
                  <div className="px-4 py-3 flex justify-between text-sm"><span className="font-medium">Bonus / Allowances</span> <span>{fmt(selectedSlip.bonus)}</span></div>
                </div>
                {/* DEDUCTIONS */}
                <div className="border-l border-gray-300">
                  <div className="bg-gray-100 px-4 py-2 font-bold border-b border-gray-300 text-gray-800">Deductions</div>
                  <div className="px-4 py-3 flex justify-between text-sm"><span className="font-medium">Leave / Absent Deductions</span> <span className="text-red-600">{fmt(selectedSlip.deductions)}</span></div>
                </div>
              </div>

              {/* Final Math */}
              <div className="flex justify-end border-t-2 border-gray-800 pt-4">
                <div className="w-1/2">
                  <div className="flex justify-between text-lg font-black text-gray-900 bg-emerald-50 p-3 rounded border border-emerald-200">
                    <span>NET PAY:</span>
                    <span className="text-emerald-700">{fmt(selectedSlip.net_pay)}</span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 text-right">This is a computer-generated document and requires no signature.</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* --- ADD THIS CSS AT THE BOTTOM TO HIDE UI WHEN PRINTING --- */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print\\:block, .print\\:block * { visibility: visible; }
          .print\\:block { position: absolute; left: 0; top: 0; width: 100%; margin: 0; box-shadow: none;}
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </PageLayout>
  );
}